import { Form, Formik, FormikProps } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import Button, { ButtonThemes } from 'ui-components/button/button';
import { LabelModes } from 'ui-components/molecules/labeled-inputs/labeledInput';
import SlideOutSidebar from 'ui-components/slide-out-sidebar/slideOutSidebar';
import XGSFormDate from 'ui-components/form/date/xgsFormDate';
import { currentOrClosestBusinessDay } from 'hooks/utils';
import XGSFormInput from 'ui-components/form/input/xgsFormInput';
import moment from 'moment';
import XGSCheckbox from 'ui-components/xgs-checkbox/xgsCheckbox';
import { weekOptions } from 'app/data/static-load/model';
import '../cloneWeek.scss'
import ImportantMessage, { ImportantMessageTheme } from 'ui-components/molecules/important-message/importantMessage';
import { useCloneWeek } from '../hooks/useCloneWeek';
import { useLanesByDay } from '../hooks/useLanes';
import Loading from 'ui-components/loading/loading';

interface CopyMoveLaneSidebarProps {
  isOpen: boolean;
  onClose: () => void;
  weekStartDate: string;
  division: string;
  onCloneSuccess?: () => void;
}

interface exclusionDay {
  dayOfWeek: string,
  fromTime: string,
  toTime: string
}

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const TargetWeek: React.FC<CopyMoveLaneSidebarProps> = ({
  isOpen,
  onClose,
  weekStartDate,
  division,
  onCloneSuccess,
}) => {
  const [targetWeekStartDate, setTargetWeekStartDate] = useState<string | undefined>();
  const [weeks, setWeeks] = useState<exclusionDay[]>([]);

  const cloneWeekMutation = useCloneWeek(() => {
    onCloneSuccess?.();
    onClose();
  });

  const { data: lanesByDay, isLoading: isLoadingSource } = useLanesByDay(
    division,
    isOpen && weekStartDate ? weekStartDate : ''
  );

  const sourceSummary = useMemo(() => {
    if (!weekStartDate) return null;

    const activeDays = Object.entries(lanesByDay ?? {})
      .filter(([, lanes]) => lanes.length > 0)
      .map(([day]) => Number(day))
      .sort((a, b) => a - b);

    const totalLanes = Object.values(lanesByDay ?? {}).reduce(
      (sum, lanes) => sum + lanes.length,
      0
    );

    const daysActive = activeDays.length > 0
      ? `${DAY_LABELS[activeDays[0] - 1]} – ${DAY_LABELS[activeDays[activeDays.length - 1] - 1]}`
      : '—';

    return {
      formattedDate: moment(weekStartDate).format('MMM D, YYYY'),
      totalLanes,
      daysActive,
    };
  }, [weekStartDate, lanesByDay]);

  useEffect(() => {
    if (!isOpen) {
      setTargetWeekStartDate(undefined);
      setWeeks([]);
    }
  }, [isOpen]);

  return <SlideOutSidebar
    show={isOpen}
    onClose={onClose}
    header="Clone Week"
    size="small"
    className="filter-sidebar"
  >
    <Formik
      initialValues={{
        targetWeekStartDate: "",
        description: "",

      }} onSubmit={(values) => {
        if (!targetWeekStartDate) return;
        const exclusions = weeks.map(({ dayOfWeek, fromTime, toTime }) => {
          const payload: any = {
            dayOfWeek: Number(dayOfWeek)
          };

          if (fromTime) payload.fromTime = fromTime;
          if (toTime) payload.toTime = toTime;

          return payload;
        });
        cloneWeekMutation.mutate({
          division: division,
          sourceWeekStartDate: weekStartDate,
          targetWeekStartDate: moment(targetWeekStartDate, "MM/DD/YYYY").format("YYYY-MM-DD"),
          targetWeekDescription: values.description.trim(),
          exclusions: exclusions
        });

      }}
      enableReinitialize
    >
      {(formikProps: FormikProps<any>) => (
        <Form>
          <div className='target-week-form'>
            <div className='target-week-source'>
              {isLoadingSource ? (
                <Loading isLoading={isLoadingSource} />
              ) : sourceSummary ? (
                <>Source: {sourceSummary.formattedDate} · {sourceSummary.totalLanes} lanes ({sourceSummary.daysActive})</>
              ) : (
                <>Source: {moment(weekStartDate).format('MMM D, YYYY')}</>
              )}
            </div>
            <div>
              <XGSFormDate
                name="targetWeekStartDate"
                label="Target Week Start Date:"
                placeholder="Select date..."
                onDateChange={(date) => { setTargetWeekStartDate(date) }}
                value={targetWeekStartDate}
                onChange={() => null}
                minDate={new Date(currentOrClosestBusinessDay())}
                required={true}
                labelMode={LabelModes.column}
                filterDate={(date) => date.getDay() === 1}
                disableWeekends={true}
              />
              <div className='target-week-form-description'>Must start on a Monday.</div></div>

            <XGSFormInput
              type="text"
              name="description"
              placeholder='Optional clone description'
              labelMode={LabelModes.column}
              label="Description"
            />
            <div className='clone-week-exclusions'>Exclude Days from Cloning</div>
            <ImportantMessage theme={ImportantMessageTheme.BLUE}>
              Check days to exclude them from being cloned.
            </ImportantMessage>
            {weekOptions.map((week, index) => {
              const day = week.value;
              const selectedDay = weeks.find(item => item.dayOfWeek === day);

              return <React.Fragment key={index}>
                <div className='target-week-exclusions-item'>
                  <XGSCheckbox
                    name={week.value}
                    checked={weeks.some(item => item.dayOfWeek === (week.value))}
                    onChange={() => {
                      const day = week.value;

                      setWeeks(prevWeeks => {
                        const exists = prevWeeks.some(item => item.dayOfWeek === day);

                        // If already selected → remove
                        if (exists) {
                          return prevWeeks.filter(item => item.dayOfWeek !== day);
                        }

                        // Else → add new exclusion day
                        return [
                          ...prevWeeks,
                          {
                            dayOfWeek: day,
                            fromTime: "",
                            toTime: ""
                          }
                        ];
                      });
                    }}
                  >
                    {week.label}
                  </XGSCheckbox>
                  <div className="update-lane-grid-item_date">
                    <div className="update-lane-grid-item_time">
                      <label style={{ fontSize: "14px", lineHeight: "24px" }}>From</label>
                      <XGSFormInput
                        name='toTime'
                        type="time"
                        className="time-picker-label"
                        value={selectedDay?.fromTime || ""}
                        onChange={(e) => {
                          const value = e.currentTarget.value;

                          setWeeks(prev =>
                            prev.map(item =>
                              item.dayOfWeek === day
                                ? { ...item, fromTime: value }
                                : item
                            )
                          );
                        }}
                      />
                    </div>

                    <div className="update-lane-grid-item_time">
                      <label style={{ fontSize: "14px", lineHeight: "24px" }}>To</label>
                      <XGSFormInput
                        name='fromTime'
                        type="time"
                        className="time-picker-label"
                        value={selectedDay?.toTime || ""}
                        onChange={(e) => {
                          const value = e.currentTarget.value;

                          setWeeks(prev =>
                            prev.map(item =>
                              item.dayOfWeek === day
                                ? { ...item, toTime: value }
                                : item
                            )
                          );
                        }}
                      />
                    </div>
                  </div>
                </div>
                <div style={{ margin: "10px 0", borderBottom: "1px solid #E0E0E0", width: "100%" }} />
              </React.Fragment>
            })}
            <div className="copy-move-lane-actions">
              <Button type="button" theme={ButtonThemes.gray} onClick={onClose}>Cancel</Button>
              <Button type="submit" theme={ButtonThemes.blue} spinner={cloneWeekMutation.isLoading} disabled={!targetWeekStartDate}>Clone Schedule</Button>
            </div>
          </div>
        </Form>
      )}
    </Formik>
  </SlideOutSidebar >;
};

export default TargetWeek;
